import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { collection, getDocs, query, orderBy, where } from 'firebase/firestore';
import { db, functions } from '../../../firebase';
import { httpsCallable } from 'firebase/functions';
import { 
  FiHome, FiUsers, FiSettings, FiMapPin, FiDollarSign, 
  FiBarChart2, FiCalendar, FiShield, FiAlertCircle,
  FiPlus, FiEdit3, FiTrash2, FiEye, FiDownload, FiX, FiTool, FiCheck
} from 'react-icons/fi';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { blockApi } from '../../../services/api';
import './FranchiseManagement.css';

const FranchiseManagement = () => {
  const { userProfile, user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [franchiseData, setFranchiseData] = useState(null);
  const [franchises, setFranchises] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Use localStorage to persist franchise selection across tab navigation (synced with FranchiseeManagement)
  const [selectedFranchise, setSelectedFranchise] = useState(() => {
    const saved = localStorage.getItem('selectedFranchiseeId');
    return saved || null;
  });
  const [editingFranchise, setEditingFranchise] = useState(null);
  const [saving, setSaving] = useState(false);

  // Timezone options for franchise selection
  const timezoneOptions = [
    { value: 'America/Los_Angeles', label: 'Pacific Time (Los Angeles)' },
    { value: 'America/Denver', label: 'Mountain Time (Denver)' },
    { value: 'America/Chicago', label: 'Central Time (Chicago)' },
    { value: 'America/New_York', label: 'Eastern Time (New York)' },
    { value: 'America/Phoenix', label: 'Arizona Time (Phoenix)' },
    { value: 'America/Anchorage', label: 'Alaska Time (Anchorage)' },
    { value: 'Pacific/Honolulu', label: 'Hawaii Time (Honolulu)' },
  ];

  // Stable function to fetch blocks data
  const fetchBlocksData = useCallback(async () => {
    try {
      const data = await blockApi.getBlocksWithStats();
      return data.blocks || [];
    } catch (err) {
      console.warn('Failed to fetch blocks data:', err.message);
      return [];
    }
  }, []);

  // Function to handle franchise editing
  const startEditingFranchise = (franchise) => {
    setEditingFranchise({
      id: franchise.id,
      name: franchise.name || '',
      contactEmail: franchise.contactEmail || '',
      contactPhone: franchise.contactPhone || '',
      address: franchise.address || '',
      timezone: franchise.timezone || 'America/Los_Angeles',
      operatingHours: franchise.operatingHours || '',
      notes: franchise.notes || ''
    });
  };

  // Function to save franchise changes
  const saveFranchiseChanges = async () => {
    if (!editingFranchise) return;

    setSaving(true);
    setError('');

    try {
      const franchiseRef = db.collection('franchises').doc(editingFranchise.id);
      await franchiseRef.update({
        name: editingFranchise.name,
        contactEmail: editingFranchise.contactEmail,
        contactPhone: editingFranchise.contactPhone,
        address: editingFranchise.address,
        timezone: editingFranchise.timezone,
        operatingHours: editingFranchise.operatingHours,
        notes: editingFranchise.notes,
        updatedAt: new Date().toISOString()
      });

      // Refresh the data by reloading the page or triggering the useEffect
      window.location.reload();
      setEditingFranchise(null);
    } catch (err) {
      setError('Failed to save franchise changes: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Function to cancel editing
  const cancelEditing = () => {
    setEditingFranchise(null);
  };

  // Function to update editing franchise field
  const updateEditingField = (field, value) => {
    setEditingFranchise(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle franchise selection with localStorage persistence (synced with FranchiseeManagement)
  const handleFranchiseSelect = (franchise) => {
    setSelectedFranchise(franchise?.id || franchise);
    // Persist selection to localStorage
    if (franchise?.id || franchise) {
      localStorage.setItem('selectedFranchiseeId', franchise?.id || franchise);
    } else {
      localStorage.removeItem('selectedFranchiseeId');
    }
  };

  // Load real franchise and block data
  useEffect(() => {
    // Don't try to load data if user is not authenticated
    if (!user) {
      setLoading(false);
      return;
    }

    const loadData = async () => {
      try {
        setLoading(true);
        setError('');
        
        // Fetch franchises from Firestore with error handling
        let franchisesData = [];
        try {
        const franchisesQuery = query(collection(db, 'franchises'), orderBy('name'));
        const franchisesSnapshot = await getDocs(franchisesQuery);
          franchisesData = franchisesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setFranchises(franchisesData);
        } catch (firestoreErr) {
          console.warn('Failed to fetch franchises:', firestoreErr.message);
          // Continue with empty franchises array
        }

        // Fetch blocks from backend with error handling
        let blocksData = [];
        try {
          blocksData = await fetchBlocksData();
          setBlocks(blocksData);
        } catch (blocksErr) {
          console.warn('Failed to fetch blocks:', blocksErr.message);
          // Continue with empty blocks array
        }

        // Set franchise data from real database data only
        if (franchisesData.length > 0) {
          // Respect saved franchise selection from localStorage
          const savedFranchiseId = localStorage.getItem('selectedFranchiseeId');
          const savedFranchise = savedFranchiseId ? 
            franchisesData.find(f => f.id === savedFranchiseId) : null;
          
          const userFranchise = franchisesData.find(f => f.ownerId === userProfile?.uid) || franchisesData[0];
          const selectedFranchiseObject = savedFranchise || userFranchise;
          
          const franchiseBlocks = blocksData.filter(block => block.assignedFranchise === selectedFranchiseObject.id) || [];
          
          // Set the selected franchise (prioritize saved selection, fallback to user's franchise)
          setSelectedFranchise(selectedFranchiseObject.id);
          
          // Save the selection to localStorage if it wasn't already saved
          if (!savedFranchise) {
            localStorage.setItem('selectedFranchiseeId', selectedFranchiseObject.id);
          }
          
          setFranchiseData({
            ...selectedFranchiseObject,
            blocks: franchiseBlocks.length,
            operators: selectedFranchiseObject.operators?.length || 0,
            revenue: selectedFranchiseObject.monthlyRevenue || 0,
            dispensesThisMonth: franchiseBlocks.reduce((total, block) => total + (block.dispensesThisMonth || 0), 0)
          });
        } else {
          // No franchises exist - show empty state
          setFranchiseData(null);
        }
        
      } catch (err) {
        console.error('Error loading franchise data:', err);
        setError(`Failed to load franchise data: ${err.message}`);
        setFranchiseData(null);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user, userProfile, fetchBlocksData]);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: FiHome },
    { id: 'operators', label: 'Operator Management', icon: FiUsers },
    { id: 'financials', label: 'Financials', icon: FiDollarSign },
    { id: 'maintenance', label: 'Maintenance', icon: FiCalendar },
    { id: 'settings', label: 'Settings', icon: FiSettings }
  ];

  if (loading) {
    return (
      <div className="franchise-management">
        <LoadingSpinner />
      </div>
    );
  }

  // Show error state with retry option
  if (error && !franchiseData) {
    return (
      <div className="franchise-management">
        <div className="error-state">
          <FiAlertCircle className="error-icon" />
          <h2>Unable to Load Franchise Data</h2>
          <p>{error}</p>
          <button 
            className="btn-primary" 
            onClick={() => {
              setError('');
              setLoading(true);
              // Trigger reload by updating a dependency
              window.location.reload();
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Show empty state when no franchise data exists
  if (!franchiseData && !loading && !error) {
    return (
      <div className="franchise-management">
        <div className="error-state">
          <FiHome className="error-icon" />
          <h2>No Franchise Data Found</h2>
          <p>No franchise information is available in the database. You may need to set up your franchise first.</p>
          <button 
            className="btn-primary" 
            onClick={() => {
              setLoading(true);
              window.location.reload();
            }}
          >
            Refresh
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="franchise-management">
      <div className="page-header">
        <div className="header-content">
          <FiHome className="header-icon" />
          <div>
            <h1>{franchiseData?.name || 'Franchise Management'}</h1>
            <p>Manage your franchise operations and team</p>
          </div>
        </div>
        <div className="header-stats">
          <div className="stat-card">
            <div className="stat-value">{franchiseData?.blocks}</div>
            <div className="stat-label">Active Blocks</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{franchiseData?.operators}</div>
            <div className="stat-label">Team Members</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">${franchiseData?.revenue?.toLocaleString()}</div>
            <div className="stat-label">Monthly Revenue</div>
          </div>
        </div>
      </div>

      <div className="tab-navigation">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <tab.icon className="tab-icon" />
            {tab.label}
          </button>
        ))}
      </div>

      <div className="tab-content">
        {activeTab === 'overview' && <OverviewTab data={franchiseData} />}
        {activeTab === 'operators' && (
          <OperatorManagementTab 
            selectedFranchise={franchises.find(f => f.id === selectedFranchise) || franchises[0]}
            franchises={franchises}
            blocks={blocks}
            onFranchiseSelect={handleFranchiseSelect}
          />
        )}

        {activeTab === 'financials' && <FinancialsTab />}
        {activeTab === 'maintenance' && <MaintenanceTab />}
        {activeTab === 'settings' && <SettingsTab data={franchiseData} />}
      </div>


    </div>
  );
};

// Tab Components
const OverviewTab = ({ data }) => (
  <div className="overview-tab">
    <div className="overview-grid">
      <div className="overview-card">
        <div className="card-header">
          <FiMapPin className="card-icon" />
          <h3>Block Network</h3>
        </div>
        <p>Manage your block locations and monitor performance across your franchise territory.</p>
        <div className="card-metrics">
          <div className="metric">
            <span className="metric-value">{data?.blocks}</span>
            <span className="metric-label">Active Blocks</span>
          </div>
          <div className="metric">
            <span className="metric-value">{data?.dispensesThisMonth}</span>
            <span className="metric-label">Dispenses This Month</span>
          </div>
        </div>
      </div>

      <div className="overview-card">
        <div className="card-header">
          <FiTool className="card-icon" />
          <h3>Maintenance</h3>
        </div>
        <p>Schedule maintenance, track service history, and manage block upkeep across your franchise.</p>
        <div className="card-actions">
          <button className="btn-primary">
            <FiCalendar className="btn-icon" />
            Schedule Service
          </button>
        </div>
      </div>

      <div className="overview-card">
        <div className="card-header">
          <FiDollarSign className="card-icon" />
          <h3>Revenue Tracking</h3>
        </div>
        <p>Monitor earnings, payment processing, and financial performance.</p>
        <div className="card-metrics">
          <div className="metric">
            <span className="metric-value">${data?.revenue?.toLocaleString()}</span>
            <span className="metric-label">This Month</span>
          </div>
        </div>
      </div>

             <div className="overview-card">
         <div className="card-header">
           <FiBarChart2 className="card-icon" />
           <h3>Analytics & Reports</h3>
         </div>
        <p>View detailed analytics and generate reports for your franchise operations.</p>
        <div className="card-actions">
          <button className="btn-secondary">
            <FiEye className="btn-icon" />
            View Analytics
          </button>
        </div>
      </div>
    </div>


  </div>
);



const OperatorManagementTab = ({ selectedFranchise, franchises, blocks, onFranchiseSelect }) => {
  const [operators, setOperators] = useState([]);
  const [franchiseBlocks, setFranchiseBlocks] = useState([]);
  const [operatorBlocks, setOperatorBlocks] = useState({});
  const [draggedBlock, setDraggedBlock] = useState(null);
  const [dragOverOperator, setDragOverOperator] = useState(null);
  const [loadingOperators, setLoadingOperators] = useState(true);
  const [assignmentError, setAssignmentError] = useState('');
  const [assignmentSuccess, setAssignmentSuccess] = useState('');

  const loadOperatorsData = useCallback(async () => {
    if (!selectedFranchise || !blocks) return;
    
    setLoadingOperators(true);
    setAssignmentError('');
    
    try {
      // Get all users who are operators in this franchise
      const operatorsSnapshot = await getDocs(
        query(
          collection(db, 'users'),
          where('globalRole', '==', 'operator')
        )
      );
      
      const franchiseOperators = operatorsSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(user => 
          user.globalRole === 'operator' && 
          user.franchiseId === selectedFranchise.id
        );
      
      setOperators(franchiseOperators);
      
      // Get blocks assigned to this franchise - refresh from backend
      const blocksData = await blockApi.getBlocksWithStats();
      const latestBlocks = blocksData.blocks || [];
      const franchiseBlocks = latestBlocks.filter(block => 
        block.assignedFranchise === selectedFranchise.id
      );
      setFranchiseBlocks(franchiseBlocks);
      
      // Get block assignments for each operator and detect duplicates
      const operatorBlockMap = {};
      const blockAssignmentTracker = {}; // Track which operators have each block
      
      for (const operator of franchiseOperators) {
        try {
          const assignmentsSnapshot = await getDocs(
            collection(db, 'users', operator.id, 'blockAssignments')
          );
          
          const assignments = assignmentsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          
          // Track duplicate assignments
          assignments.forEach(assignment => {
            const serial = assignment.serial;
            if (!blockAssignmentTracker[serial]) {
              blockAssignmentTracker[serial] = [];
            }
            blockAssignmentTracker[serial].push({
              operatorId: operator.id,
              operatorName: operator.displayName,
              assignmentDocId: assignment.id
            });
          });
          
          operatorBlockMap[operator.id] = assignments;
        } catch (err) {
          console.warn(`Failed to load assignments for operator ${operator.id}:`, err);
          operatorBlockMap[operator.id] = [];
        }
      }
      
      // Note: It's valid for blocks to be assigned to multiple operators (multiple operators per site)
      // Just log for debugging purposes
      Object.entries(blockAssignmentTracker).forEach(([serial, operators]) => {
        if (operators.length > 1) {
          console.log('Block assigned to multiple operators (this is valid):', {
            blockSerial: serial,
            operatorCount: operators.length,
            operators: operators.map(op => op.operatorName)
          });
        }
      });
      setOperatorBlocks(operatorBlockMap);
      
    } catch (err) {
      console.error('Failed to load operators data:', err);
      setAssignmentError('Failed to load operators data: ' + err.message);
    } finally {
      setLoadingOperators(false);
    }
  }, [selectedFranchise, blocks, blockApi]);
  
  useEffect(() => {
    loadOperatorsData();
  }, [loadOperatorsData]);

  // Drag and drop handlers
  const handleBlockDragStart = (e, block) => {
    setDraggedBlock(block);
    e.dataTransfer.effectAllowed = 'move';
    e.target.style.opacity = '0.5';
  };

  const handleBlockDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedBlock(null);
    setDragOverOperator(null);
  };

  const handleOperatorDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleOperatorDragEnter = (e, operatorId) => {
    e.preventDefault();
    setDragOverOperator(operatorId);
  };

  const handleOperatorDragLeave = (e) => {
    e.preventDefault();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverOperator(null);
    }
  };

  const handleOperatorDrop = async (e, operatorId) => {
    e.preventDefault();
    setDragOverOperator(null);
    setAssignmentError('');
    setAssignmentSuccess('');

    if (!draggedBlock) return;

    try {
      const userOperations = httpsCallable(functions, 'userOperations');
      let result;
      
      if (operatorId === 'unassigned') {
        // Unassigning from an operator
        const blockSerial = draggedBlock.serial || draggedBlock.id;
        
        console.log('=== UNASSIGNMENT ATTEMPT START ===');
        console.log('Block to unassign:', blockSerial);
        console.log('Dragged block data:', draggedBlock);
        console.log('Target operator ID:', operatorId);
        
        try {
          // First, try to get the current block data from backend
          console.log('Fetching fresh block data from backend...');
          const blocksData = await blockApi.getBlocksWithStats();
          const currentBlock = blocksData.blocks?.find(b => (b.serial || b.id) === blockSerial);
          
          console.log('Fresh block data:', currentBlock);
          
          if (!currentBlock) {
            console.error('Block not found in backend data');
            setAssignmentError('Block not found in backend');
            return;
          }
          
          // If assignedOperator is null, we need to use the operator ID from the drag context
          // This handles the case where the blocks collection is out of sync with blockAssignments
          let operatorIdToUnassign = currentBlock.assignedOperator;
          
          if (!operatorIdToUnassign && draggedBlock.currentOperatorId) {
            // The block's assignedOperator is null, but we know it's assigned based on the UI
            // Use the operator ID from the drag context
            operatorIdToUnassign = draggedBlock.currentOperatorId;
            console.warn('⚠️ Block assignedOperator is null, using operator ID from drag context:', {
              blockSerial: blockSerial,
              operatorIdFromDrag: draggedBlock.currentOperatorId,
              assignedOperatorField: currentBlock.assignedOperator
            });
          }
          
          if (!operatorIdToUnassign) {
            console.error('Cannot determine operator ID for unassignment');
            setAssignmentError('Cannot determine which operator to unassign block from');
            return;
          }
          
          // Debug logging
          console.log('📋 Unassignment details:', {
            blockSerial: blockSerial,
            operatorIdFromDrag: draggedBlock.currentOperatorId,
            operatorIdFromFreshData: currentBlock.assignedOperator,
            operatorIdUsing: operatorIdToUnassign,
            isUsingDragContext: !currentBlock.assignedOperator,
            timestamp: new Date().toISOString()
          });
          
          console.log('Calling backend unassignment with params:', {
            operation: 'unassignBlocksFromOperator',
            operatorId: operatorIdToUnassign,
            blockSerials: [blockSerial],
            franchiseId: selectedFranchise.id
          });
          
          result = await userOperations({
            operation: 'unassignBlocksFromOperator',
            operatorId: operatorIdToUnassign,
            blockSerials: [blockSerial],
            franchiseId: selectedFranchise.id
          });
          
          console.log('Backend unassignment result:', result);
        } catch (error) {
          console.error('❌ Error during unassignment process:', error);
          console.error('Error details:', {
            message: error.message,
            code: error.code,
            timestamp: new Date().toISOString()
          });
          
          // Fallback to using the operator ID from the dragged block
          if (!draggedBlock.currentOperatorId) {
            setAssignmentError('Cannot determine which operator to unassign block from');
            return;
          }
          
          console.log('Attempting fallback unassignment with drag context operator ID...');
          try {
            result = await userOperations({
              operation: 'unassignBlocksFromOperator',
              operatorId: draggedBlock.currentOperatorId,
              blockSerials: [blockSerial],
              franchiseId: selectedFranchise.id
            });
            console.log('Fallback unassignment result:', result);
          } catch (fallbackError) {
            console.error('❌ Fallback also failed:', fallbackError);
            throw fallbackError;
          }
        }
      } else {
        // Assign the block to the specified operator
        result = await userOperations({
          operation: 'assignBlocksToOperator',
          operatorId: operatorId,
          blockSerials: [draggedBlock.serial || draggedBlock.id],
          franchiseId: selectedFranchise.id
        });
      }

      if (result.data && result.data.success) {
        if (operatorId === 'unassigned') {
          setAssignmentSuccess(`Successfully unassigned ${draggedBlock.name || draggedBlock.serial} from operator!`);
        } else {
          const operator = operators.find(op => op.id === operatorId);
          setAssignmentSuccess(`Successfully assigned ${draggedBlock.name || draggedBlock.serial} to ${operator?.displayName}!`);
        }
        
        // Always refresh all data to ensure consistency
        // Add a small delay to ensure backend has completed updates
        setTimeout(async () => {
          await loadOperatorsData();
        }, 1000);
      } else {
        throw new Error(result.data?.message || 'Failed to assign block');
      }
    } catch (err) {
      console.error('Block assignment error:', err);
      setAssignmentError('Failed to assign block: ' + err.message);
    }

    setDraggedBlock(null);
  };

  // Get available blocks (not assigned to any operator)
  const getAvailableBlocks = () => {
    const assignedBlockSerials = new Set();
    Object.values(operatorBlocks).forEach(assignments => {
      assignments.forEach(assignment => {
        assignedBlockSerials.add(assignment.serial);
      });
    });
    
    // Also check the assignedOperator field from blocks collection for consistency
    return franchiseBlocks.filter(block => {
      const serial = block.serial || block.id;
      // Block is available if:
      // 1. Not in any operator's blockAssignments subcollection
      // 2. AND doesn't have assignedOperator field set
      return !assignedBlockSerials.has(serial) && !block.assignedOperator;
    });
  };

  if (loadingOperators) {
    return (
      <div className="operator-management-tab">
        <LoadingSpinner message="Loading operators..." />
      </div>
    );
  }

  const availableBlocks = getAvailableBlocks();

  // Handle case where no franchise is selected
  if (!selectedFranchise) {
    return (
      <div className="operator-management-tab">
        <div className="empty-state">
          <FiMapPin className="empty-icon" />
          <h3>No Franchise Selected</h3>
          <p>Please wait while we load your franchise data.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="operator-management-tab">
      {/* Block Assignment Header */}
      <div className="assignment-header">
        <h2>Block Assignment - {selectedFranchise?.name}</h2>
        <p>Drag blocks from "Available Blocks" to assign them to specific operators</p>
      </div>

      {assignmentError && (
        <div className="alert alert-error">
          <FiAlertCircle className="alert-icon" />
          {assignmentError}
        </div>
      )}

      {assignmentSuccess && (
        <div className="alert alert-success">
          <FiCheck className="alert-icon" />
          {assignmentSuccess}
        </div>
      )}

      <div className="assignment-grid">
        {/* Available Blocks Column */}
        <div className="assignment-column">
          <h3>Available Blocks ({availableBlocks.length})</h3>
          <div className="blocks-container">
            {availableBlocks.length === 0 ? (
              <div className="empty-state">
                <FiMapPin className="empty-icon" />
                <p>All blocks are assigned to operators</p>
              </div>
            ) : (
              availableBlocks.map(block => (
                <div
                  key={block.id}
                  className={`draggable-block ${draggedBlock?.id === block.id ? 'dragging' : ''}`}
                  draggable
                  onDragStart={(e) => handleBlockDragStart(e, block)}
                  onDragEnd={handleBlockDragEnd}
                >
                  <div className="drag-handle">
                    <span className="drag-icon">⋮⋮</span>
                  </div>
                  <div className="block-content">
                    <div className="block-header">
                      <span className="block-serial">{block.serial || block.id}</span>
                      <span className="status-badge available">Available</span>
                    </div>
                    <h5>{block.name}</h5>
                    <p>{block.location}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Operators Column */}
        <div className="assignment-column">
          <h3>Operators ({operators.length})</h3>
          <div className="operators-container">
            {operators.length === 0 ? (
              <div className="empty-state">
                <FiUsers className="empty-icon" />
                <p>No operators assigned to this franchise</p>
              </div>
            ) : (
              operators.map(operator => {
                const assignments = operatorBlocks[operator.id] || [];
                const isDropTarget = dragOverOperator === operator.id;
                
                return (
                  <div
                    key={operator.id}
                    className={`operator-card ${isDropTarget ? 'drag-over' : ''}`}
                    onDragOver={handleOperatorDragOver}
                    onDragEnter={(e) => handleOperatorDragEnter(e, operator.id)}
                    onDragLeave={handleOperatorDragLeave}
                    onDrop={(e) => handleOperatorDrop(e, operator.id)}
                  >
                    <div className="operator-header">
                      <FiUsers className="operator-icon" />
                      <div className="operator-info">
                        <h4>{operator.displayName}</h4>
                        <p>{operator.email}</p>
                      </div>
                      <span className="assignment-count">{assignments.length} blocks</span>
                    </div>
                    
                    <div className="assigned-blocks">
                      {assignments.length === 0 ? (
                        <p className="no-assignments">No blocks assigned</p>
                      ) : (
                        assignments.map(assignment => {
                          const block = franchiseBlocks.find(b => 
                            (b.serial || b.id) === assignment.serial
                          );
                          
                          // Note: assignedOperator field may be null or out of sync
                          // This is handled in the unassignment logic
                          
                          // Create a block object for dragging
                          const assignedBlockData = {
                            serial: assignment.serial,
                            name: block?.name || assignment.serial,
                            id: assignment.serial,
                            // Use the block's assignedOperator from backend data, fallback to operator.id
                            currentOperatorId: block?.assignedOperator || operator.id,
                            // Store both IDs for debugging
                            _operatorIdFromAssignment: operator.id,
                            _operatorIdFromBlock: block?.assignedOperator
                          };
                          
                          return (
                            <div 
                              key={assignment.serial} 
                              className="assigned-block draggable"
                              draggable={true}
                              onDragStart={(e) => handleBlockDragStart(e, assignedBlockData)}
                              onDragEnd={handleBlockDragEnd}
                            >
                              <div className="drag-handle">
                                <span className="drag-icon">⋮⋮</span>
                              </div>
                              <div className="block-content">
                                <span className="block-name">
                                  {block?.name || assignment.serial}
                                </span>
                                <span className="block-serial">{assignment.serial}</span>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                    
                    {isDropTarget && (
                      <div className="drop-indicator">
                        Drop block here to assign
                      </div>
                    )}
                  </div>
                );
              })
            )}
            
            {/* Unassigned Drop Zone */}
            <div
              className={`unassigned-drop-zone ${dragOverOperator === 'unassigned' ? 'drag-over' : ''}`}
              onDragOver={handleOperatorDragOver}
              onDragEnter={(e) => handleOperatorDragEnter(e, 'unassigned')}
              onDragLeave={handleOperatorDragLeave}
              onDrop={(e) => handleOperatorDrop(e, 'unassigned')}
            >
              <div className="unassigned-content">
                <FiX className="unassigned-icon" />
                <h4>Unassigned</h4>
                <p>Drag blocks here to remove them from operators</p>
                {dragOverOperator === 'unassigned' && (
                  <div className="drop-indicator">
                    Drop to unassign
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const FinancialsTab = () => (
  <div className="financials-tab">
    <h2>Financial Overview</h2>
    <div className="coming-soon-section">
      <FiDollarSign className="coming-soon-icon" />
      <h3>Revenue Overview</h3>
      <p>Basic financial overview features:</p>
      <ul>
        <li>Revenue tracking and analytics</li>
        <li>Financial reporting and exports</li>
        <li>Performance summaries</li>
      </ul>
    </div>


  </div>
);

const MaintenanceTab = () => (
  <div className="maintenance-tab">
    <h2>Maintenance Management</h2>
    <div className="coming-soon-section">
      <FiCalendar className="coming-soon-icon" />
      <h3>Maintenance Scheduling & Tracking</h3>
      <p>Maintenance management features in development:</p>
      <ul>
        <li>Scheduled maintenance reminders</li>
        <li>Maintenance history tracking</li>
        <li>Service provider management</li>
        <li>Parts inventory tracking</li>
        <li>Maintenance cost analysis</li>
      </ul>
    </div>


  </div>
);

const SettingsTab = ({ data }) => (
  <div className="settings-tab">
    <h2>Franchise Settings</h2>
    <div className="settings-grid">
      <div className="settings-section">
        <h3>Franchise Information</h3>
        <div className="form-group">
          <label>Franchise Name</label>
          <input type="text" value={data?.name || ''} disabled />
        </div>
        <div className="form-group">
          <label>Owner</label>
          <input type="text" value={data?.owner || ''} disabled />
        </div>
        <div className="form-group">
          <label>Location</label>
          <input type="text" value={data?.location || ''} disabled />
        </div>
      </div>

      <div className="settings-section">
        <h3>Operational Settings</h3>
        <div className="form-group">
          <label>Default Pricing</label>
          <input type="text" placeholder="Configure pricing rules" disabled />
        </div>
        <div className="form-group">
          <label>Operating Hours</label>
          <input type="text" placeholder="Set operating hours" disabled />
        </div>
        <div className="form-group">
          <label>Notification Preferences</label>
          <input type="text" placeholder="Configure notifications" disabled />
        </div>
      </div>
    </div>

    <div className="coming-soon-notice">
      <FiSettings className="notice-icon" />
      <p>Advanced franchise settings will be available in a future update.</p>
    </div>


  </div>
);

export default FranchiseManagement; 
